/**
 * sentry.js — Sentry initialization for the frontend.
 *
 * ENV-GATED: if REACT_APP_SENTRY_DSN is empty/missing, this file is a no-op.
 * The SDK never initializes and zero network requests are made → zero cost
 * until you paste a DSN into frontend/.env.
 *
 * Imported FIRST in src/index.js so global error handlers + performance
 * tracing are wired before any other application code runs.
 *
 * To enable in prod: set REACT_APP_SENTRY_DSN=https://...@o0.ingest.sentry.io/0
 * Optional: REACT_APP_SENTRY_ENVIRONMENT (defaults to 'development'),
 *           REACT_APP_SENTRY_RELEASE (commit SHA / build ID),
 *           REACT_APP_SENTRY_TRACES_SAMPLE_RATE (default 0.1 in prod, 1.0 dev).
 */
import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development';
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE;
const isProd = ENV === 'production';
const TRACES_RATE = (() => {
  const raw = (process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '').trim();
  if (!raw) return isProd ? 0.1 : 1.0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : (isProd ? 0.1 : 1.0);
})();

export const SENTRY_ENABLED = Boolean(DSN && DSN.trim().length > 0);

if (SENTRY_ENABLED) {
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay is opt-in via env to keep the free-tier quota safe.
      ...(process.env.REACT_APP_SENTRY_REPLAY === '1'
        ? [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })]
        : []),
    ],
    tracesSampleRate: TRACES_RATE,
    replaysSessionSampleRate: 0,   // never record full sessions
    replaysOnErrorSampleRate: 0.5, // record 50% of error sessions if Replay is enabled
    // Drop noise: cancelled fetches, ad-blockers, browser extensions.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Non-Error promise rejection captured',
      /AbortError/i,
      /Network request failed/i,
    ],
  });
}

/**
 * captureException(err, { tags, extra }) — drop-in helper for our _swallow()
 * pattern. Uses the scope-callback form so tags pass TypeScript typing cleanly
 * and don't conflict with EventHint semantics.
 * No-op when SENTRY_ENABLED is false.
 */
export function captureException(error, { tags, extra } = {}) {
  if (!SENTRY_ENABLED) return;
  try {
    Sentry.captureException(error, (scope) => {
      if (tags) for (const [k, v] of Object.entries(tags)) scope.setTag(k, String(v));
      if (extra) for (const [k, v] of Object.entries(extra)) scope.setExtra(k, v);
      return scope;
    });
  } catch (_) { /* never let Sentry crash the app */ }
}

/**
 * Attach (or clear) user identity on the active Sentry scope.
 * Called from AuthContext on login/logout.
 */
export function setSentryUser(user) {
  if (!SENTRY_ENABLED) return;
  try {
    if (user && user.id) {
      Sentry.setUser({ id: user.id, username: user.phone });
      Sentry.setTag('user_role', user.role || 'manager');
    } else {
      Sentry.setUser(null);
    }
  } catch (_) { /* noop */ }
}
